/**
 * SearchInput - Real input with debounce, clear button, and submit support
 */
import { useState, useEffect, useCallback, useRef, memo } from 'react';
import { Search, X } from 'lucide-react';
import { cn } from '@/lib/utils';

interface SearchInputProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  debounceMs?: number;
  className?: string;
  autoFocus?: boolean;
}

export const SearchInput = memo(function SearchInput({
  value,
  onChange,
  placeholder = 'Suche nach Restaurants...',
  debounceMs = 300,
  className,
  autoFocus = false
}: SearchInputProps) {
  const [localValue, setLocalValue] = useState(value);
  const inputRef = useRef<HTMLInputElement>(null);
  const debounceRef = useRef<NodeJS.Timeout>();

  // Sync external value changes
  useEffect(() => {
    setLocalValue(value);
  }, [value]);

  // Debounced onChange
  const handleChange = useCallback((newValue: string) => {
    setLocalValue(newValue);
    
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }
    
    debounceRef.current = setTimeout(() => {
      onChange(newValue);
    }, debounceMs);
  }, [onChange, debounceMs]);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
    };
  }, []);

  // Clear handler
  const handleClear = useCallback(() => {
    setLocalValue('');
    onChange('');
    inputRef.current?.focus();
  }, [onChange]);

  // Submit handler (Enter key)
  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
      onChange(localValue);
    }
  }, [onChange, localValue]);

  return (
    <div 
      className={cn(
        "relative flex items-center gap-3 px-4 py-3 rounded-xl",
        "transition-shadow duration-200",
        "focus-within:ring-2 focus-within:ring-[#009544]/20",
        className
      )}
    >
      <Search className="w-5 h-5 text-gray-400 flex-shrink-0" />
      <input
        ref={inputRef}
        type="text"
        value={localValue}
        onChange={(e) => handleChange(e.target.value)}
        onKeyDown={handleKeyDown}
        placeholder={placeholder}
        autoFocus={autoFocus}
        className="flex-1 bg-transparent text-sm text-gray-900 placeholder:text-gray-400 outline-none min-w-0"
      />
      {localValue && (
        <button
          type="button"
          onClick={handleClear}
          className="flex-shrink-0 w-6 h-6 flex items-center justify-center rounded-full bg-gray-100 hover:bg-gray-200 transition-colors"
          aria-label="Suche löschen"
        >
          <X className="w-3.5 h-3.5 text-gray-500" />
        </button>
      )}
    </div>
  );
});
